import { FC, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import moment from 'moment';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useFiltersStore } from '@/stores/filtersStore';
import { useGlobalStore } from '@/stores/globalStore';

import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

interface FilterBadgesProps {
  currentPage: 'search' | 'dashboard';
}

const FilterBadges: FC<FilterBadgesProps> = ({ currentPage }) => {
  const {
    getFilterByIds,
    clearFilterByIdsByValue,
    clearFilterByIds,
    clearFilterByIdsByValues,
  } = useFiltersStore();
  const { isRootLevel } = useFileExplorerStore();
  const { insuranceObjects, directories, getInsuranceObjects } =
    useApiManagerStore();

  const { steps } = useGlobalStore();

  const [dialogOpen, setDialogOpen] = useState(false);

  const entries = useMemo(
    () =>
      Object.entries(getFilterByIds(currentPage) ?? {}).flatMap(
        ([key, value]) => ({
          label: value.filterCategory,
          filterByValues: value.filterByValues,
          field: key,
        }),
      ),
    [Object.entries(getFilterByIds(currentPage))],
  );
  const totalCount = useMemo(() => {
    let count = 0;

    entries.forEach((item) => {
      switch (item.field) {
        case 'step':
          count += [
            ...new Set(
              steps
                .filter((s) =>
                  item.filterByValues.some((f) => f.value === s.uuid),
                )
                .map((s) => s.status_name),
            ),
          ].length;
          break;
        case 'created':
        case 'modified':
          count += item.filterByValues.length / 2;
          break;
        case 'insurance_object':
          count += directories.filter((d) =>
            insuranceObjects
              .filter((io) =>
                item.filterByValues.some((f) => f.value === io.uuid),
              )
              .map((io) => io.directory)
              .includes(d.uuid),
          ).length;
          break;

        default:
          count += item.filterByValues.length;
      }
    });

    return count;
  }, [entries, steps, insuranceObjects, directories]);

  const handleClearDirectory = async (directoryUuid: string) => {
    const insuranceObjectsFromFiltersUuids = getFilterByIds(
      currentPage,
    ).insurance_object.filterByValues.map((filter) => filter.value as string);
    const insuranceObjectsFromFilters = await getInsuranceObjects(
      insuranceObjectsFromFiltersUuids,
    );

    const insuranceObjectsFiltered = insuranceObjectsFromFilters.filter(
      (insuranceObject) => insuranceObject.directory === directoryUuid,
    );

    clearFilterByIdsByValues(
      'insurance_object',
      insuranceObjectsFiltered.map((insuranceObject) => insuranceObject.uuid),
      currentPage,
    );
  };

  const handleClearStep = (stepStatusName: string) => {
    const stepsFiltered = steps.filter(
      (step) => step.status_name === stepStatusName && !step.is_deleted,
    );

    clearFilterByIdsByValues(
      'step',
      stepsFiltered.map((step) => step.uuid),
      currentPage,
    );
  };

  const renderLabelForFilter = (item: {
    label: string;
    field: string;
    filterByValues: { label: string; value: string | Date }[];
  }): React.ReactNode => {
    switch (item.field) {
      case 'insurance_object':
        const insuranceObjectsFiltered = insuranceObjects.filter(
          (insuranceObject) =>
            item.filterByValues.some(
              (filter) => filter.value === insuranceObject.uuid,
            ),
        );
        const directoryFromInsuranceObject = directories.filter((directory) =>
          insuranceObjectsFiltered.some(
            (insuranceObject) => insuranceObject.directory === directory.uuid,
          ),
        );
        return directoryFromInsuranceObject.map((directory) => (
          <Badge
            key={directory.uuid}
            variant="secondary"
            className="h-6 gap-1 rounded border-separator bg-white px-1 py-1 text-sm font-normal text-primary"
          >
            <span>{`${item.label}: ${directory.abbreviation}`}</span>
            <X
              className="h-3 w-3 cursor-pointer"
              onClick={() => handleClearDirectory(directory.uuid)}
            />
          </Badge>
        ));

      case 'created':
      case 'modified':
        return (
          <Badge
            key={item.field}
            variant="secondary"
            className="h-6 gap-1 rounded border-separator bg-white px-1 py-1 text-sm font-normal text-primary"
          >
            <span>{`${item.label}: ${moment(item.filterByValues[0].value).format('DD.MM.YYYY')} - ${moment(item.filterByValues[1].value).format('DD.MM.YYYY')}`}</span>
            <X
              className="h-3 w-3 cursor-pointer"
              onClick={() => {
                clearFilterByIds(item.field, currentPage);
              }}
            />
          </Badge>
        );

      case 'step':
        const stepsFiltered = [
          ...new Set(
            steps
              .filter(
                (step) =>
                  !step.is_deleted &&
                  item.filterByValues.some(
                    (filter) => filter.value === step.uuid,
                  ),
              )
              .map((step) => step.status_name),
          ),
        ].map((step) => ({
          label: step,
          value: step,
        }));

        return stepsFiltered.map((step) => (
          <Badge
            key={step.value}
            variant="secondary"
            className="h-6 gap-1 rounded border-separator bg-white px-1 py-1 text-sm font-normal text-primary"
          >
            <span>{`${item.label}: ${step.label}`}</span>
            <X
              className="h-3 w-3 cursor-pointer"
              onClick={() => handleClearStep(step.value)}
            />
          </Badge>
        ));

      default:
        return item.filterByValues.map((filter) => (
          <Badge
            key={filter.value.toString()}
            variant="secondary"
            className="h-6 gap-1 rounded border-separator bg-white px-1 py-1 text-sm font-normal text-primary"
          >
            <span>{`${item.label}: ${filter.label}`}</span>
            <X
              className="h-3 w-3 cursor-pointer"
              onClick={() => {
                clearFilterByIdsByValue(item.field, filter.value, currentPage);
              }}
            />
          </Badge>
        ));
    }
  };

  if (!getFilterByIds(currentPage) || totalCount === 0 || !isRootLevel)
    return null;

  return (
    <div className="flex flex-wrap gap-2 p-2">
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <button
          type="button"
          className="rounded border border-separator bg-white px-2 py-1 text-sm text-primary hover:bg-neutral-50"
          aria-label={`Show all ${totalCount} filters`}
          onClick={() => setDialogOpen(true)}
        >
          Застосовані фільтри (+{totalCount})
        </button>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Застосовані фільтри ({totalCount})</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <div className="flex flex-wrap gap-2">
              {entries.map((item) => renderLabelForFilter(item))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FilterBadges;
